import { useMemo, useState } from "react";
import { OrganizationIntegrationAccountItem } from "@common/schemas/organization-connection-groups.schema";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useUser } from "@hooks/useUser";
import tableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { integrationRowActions } from "./integrationRowActions";
import { useIntegrationRowActions } from "./IntegrationRowActionsContext";
import { useOrgReconnect } from "./OrgReconnectContext";
import { RemoveIntegrationAccountModal } from "./RemoveIntegrationAccountModal";
import { ReconnectReminderPreviewModal } from "./ReconnectReminderPreviewModal";
import styles from "./IntegrationRowActionsCell.module.scss";

interface IntegrationRowActionsCellProps {
    item: OrganizationIntegrationAccountItem;
}

/**
 * The kebab on a normal row. Group headers never reach here — `renderGroupByCell`
 * returns null for them in every non-expand column.
 *
 * `captureClicks` stops a click on the trigger from also selecting the row.
 */
export const IntegrationRowActionsCell = ({
    item,
}: IntegrationRowActionsCellProps) => {
    const currentUser = useUser();
    const { remindedConnectionIds } = useIntegrationRowActions();
    const { reconnect, reconnectingConnectionIds } = useOrgReconnect();
    const [isRemoving, setIsRemoving] = useState(false);
    const [isReminding, setIsReminding] = useState(false);

    const isReconnecting = reconnectingConnectionIds.has(item.connection.id);

    const actions = useMemo(
        () =>
            integrationRowActions({
                item,
                currentUserId: currentUser?.id ?? null,
                remindedConnectionIds,
            }),
        [item, currentUser?.id, remindedConnectionIds],
    );

    const hasActionAboveRemove =
        actions.reconnect.isVisible || actions.remind.isVisible;

    return (
        <ViewTableCell captureClicks className={styles.actionsCell}>
            {/* Bordered rather than bare, matching the kebab on the org
                Clients table. */}
            <Menu.More variant="primary" className={tableStyles.showOnHover}>
                {actions.reconnect.isVisible ? (
                    <Menu.Item
                        // Deliberately still clickable while in flight: an
                        // abandoned OAuth popup fires no close event, so
                        // re-clicking is the only way back out of it.
                        disabled={!actions.reconnect.isEnabled}
                        disabledReason={actions.reconnect.disabledReason}
                        onClick={() => reconnect(item)}
                    >
                        {isReconnecting ? "Reconnecting…" : "Reconnect"}
                    </Menu.Item>
                ) : null}

                {actions.remind.isVisible ? (
                    <Menu.Item
                        disabled={!actions.remind.isEnabled}
                        disabledReason={actions.remind.disabledReason}
                        onClick={() => setIsReminding(true)}
                    >
                        Email client to reconnect
                    </Menu.Item>
                ) : null}

                {/* Removal is destructive, so it sits below a divider rather
                    than flush against the action above it. Guarded because the
                    separator would otherwise lead the menu if neither item
                    above renders. */}
                {hasActionAboveRemove ? <Menu.Separator /> : null}

                <Menu.Item
                    destructive
                    disabled={!actions.remove.isEnabled}
                    disabledReason={actions.remove.disabledReason}
                    onClick={() => setIsRemoving(true)}
                >
                    Remove
                </Menu.Item>
            </Menu.More>

            {isRemoving ? (
                <RemoveIntegrationAccountModal
                    item={item}
                    show
                    onClose={() => setIsRemoving(false)}
                />
            ) : null}

            {isReminding ? (
                <ReconnectReminderPreviewModal
                    item={item}
                    show
                    onClose={() => setIsReminding(false)}
                />
            ) : null}
        </ViewTableCell>
    );
};
